import { SelectProvider, useSelect } from "@mui/base";
import * as React from "react";
import type { FunctionComponent } from "react";

import { DropdownSelectOption } from "./option";
import {
  Button,
  Container,
  Listbox,
  NativeSelectWrapper,
  Select,
  StyledIconQuickLink,
} from "./styles";

export type Props = {
  isNativeSelect: boolean;
  onChange: (value: string) => void;
  options: DropdownSelectOption[];
};

type DropdownSelectOption = {
  isDefault: boolean;
  isSelected: boolean;
  label: string;
  value: string;
};

export const DropdownSelect: FunctionComponent<Props> = ({
  isNativeSelect = false,
  onChange,
  options,
}) => {
  const defaultOption = options.find(option => option.isDefault === true);

  const renderSelectedValue = (value: null | string, options: DropdownSelectOption[]) => {
    const selectedOption = options.find(option => option.value === value);
    return selectedOption ? selectedOption.label : null;
  };

  const listboxReference = React.useRef<HTMLUListElement>(null);
  const [listboxVisible, setListboxVisible] = React.useState(false);

  const { contextValue, getButtonProps, getListboxProps, value } = useSelect({
    defaultValue: defaultOption?.value,
    listboxRef: listboxReference,
    onChange: (event_, value) => {
      if (typeof value === "string") {
        onChange(value);
      }
    },
    onOpenChange: setListboxVisible,
    open: listboxVisible,
    options,
  });

  const renderNativeSelect = () => (
    <NativeSelectWrapper>
      <Button>
        {defaultOption?.label}

        <StyledIconQuickLink />
      </Button>

      <Select
        onChange={event => {
          const value = event.target.value;
          if (typeof value === "string") {
            onChange(value);
          }
        }}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </NativeSelectWrapper>
  );

  if (isNativeSelect) {
    return renderNativeSelect();
  }

  return (
    <Container>
      <Button {...getButtonProps()}>
        {renderSelectedValue(value, options)}
        <StyledIconQuickLink />
      </Button>
      <Listbox {...getListboxProps()} $hidden={!listboxVisible}>
        <SelectProvider value={contextValue}>
          {options.map(option => (
            <DropdownSelectOption
              isSelected={option.isSelected}
              key={option.value}
              value={option.value}
            >
              {option.label}
            </DropdownSelectOption>
          ))}
        </SelectProvider>
      </Listbox>
    </Container>
  );
};

DropdownSelect.displayName = "DropdownSelect";
